import { useEffect, useRef, useState } from "react";
import { Container } from "@mui/system";
import { useLanguage } from "../../i18n/LanguageContext";
import DoorScene from "./scenes/DoorScene";
import VoiceScene from "./scenes/VoiceScene";
import DndScene from "./scenes/DndScene";
import PresenceScene from "./scenes/PresenceScene";
import "./smartKeyHero.css";

// Scene stage is drawn at a fixed size and scaled to fit the available width.
const STAGE_WIDTH = 560;
const STAGE_HEIGHT = 400;

const slides = [
    { Scene: DoorScene, duration: 8000 },
    { Scene: VoiceScene, duration: 9000 },
    { Scene: DndScene, duration: 7000 },
    { Scene: PresenceScene, duration: 12000 },
];

const copy = {
    en: [
        {
            label: "Room key",
            eyebrow: "Tarqia Smart Key",
            title: <>Your guests' phone is now their <span>room key</span></>,
            text: "Phone, card or smartwatch, and the door opens. Smart Key upgrades the locks you already have and brings check-in, guest requests and housekeeping into one platform, branded for your hotel.",
        },
        {
            label: "AI guest orders",
            eyebrow: "AI voice assistant",
            title: <>Just ask. <span>Smart Key</span> handles the rest</>,
            text: "Guests order by voice or text, in English or Arabic. The AI understands the request, sends it to the right department and keeps the guest updated until it is done.",
        },
        {
            label: "Do Not Disturb",
            eyebrow: "Do Not Disturb",
            title: <>Privacy with <span>one tap</span></>,
            text: "Guests switch on Do Not Disturb from their phone and housekeeping sees it instantly on their console, so nobody knocks at the wrong time.",
        },
        {
            label: "Room sensors",
            eyebrow: "Smart room sensors",
            title: <>Know when a room is <span>occupied</span></>,
            text: "Motion and heat sensors tell staff whether a room is empty, occupied or the guest is resting, so housekeeping arrives at the right time and an alert is raised if there is no movement for too long.",
        },
    ],
    ar: [
        {
            label: "مفتاح الغرفة",
            eyebrow: "ضياف من ترقية",
            title: <>هاتف ضيفك أصبح <span>مفتاح غرفته</span></>,
            text: "بالهاتف أو البطاقة أو الساعة الذكية يُفتح الباب. يطوّر ضياف الأقفال الموجودة لديك، ويجمع تسجيل الوصول وطلبات الضيوف والتدبير المنزلي في منصة واحدة بهوية فندقك.",
        },
        {
            label: "طلبات الضيوف الذكية",
            eyebrow: "مساعد صوتي ذكي",
            title: <>اطلب فقط، و<span>ضياف</span> يتولى الباقي</>,
            text: "يطلب الضيوف صوتاً أو كتابةً بالعربية أو الإنجليزية، فيفهم الذكاء الاصطناعي الطلب ويرسله إلى القسم المختص، ويُبقي الضيف على اطلاع حتى يكتمل.",
        },
        {
            label: "عدم الإزعاج",
            eyebrow: "عدم الإزعاج",
            title: <>الخصوصية <span>بلمسة واحدة</span></>,
            text: "يفعّل الضيف خاصية عدم الإزعاج من هاتفه، فتظهر فوراً لفريق التدبير المنزلي على لوحة التحكم، فلا يطرق أحد الباب في وقت غير مناسب.",
        },
        {
            label: "مستشعرات الغرفة",
            eyebrow: "مستشعرات الغرفة الذكية",
            title: <>اعرف متى تكون الغرفة <span>مشغولة</span></>,
            text: "تُعلم مستشعرات الحركة والحرارة فريقك إن كانت الغرفة شاغرة أو مشغولة أو أن الضيف يستريح، ليصل فريق التنظيف في الوقت المناسب، ويُرسَل تنبيه إذا لم تُرصد أي حركة لفترة طويلة.",
        },
    ],
};

const SmartKeyHero = () => {
    const { lang } = useLanguage();
    const [active, setActive] = useState(0);
    const [scale, setScale] = useState(1);
    const sceneRef = useRef(null);

    useEffect(() => {
        const timer = setTimeout(() => setActive((current) => (current + 1) % slides.length), slides[active].duration);
        return () => clearTimeout(timer);
    }, [active]);

    useEffect(() => {
        const scene = sceneRef.current;
        if (!scene) return;
        const observer = new ResizeObserver(([entry]) => {
            setScale(Math.min(1, entry.contentRect.width / STAGE_WIDTH));
        });
        observer.observe(scene);
        return () => observer.disconnect();
    }, []);

    const slide = copy[lang][active];
    const Scene = slides[active].Scene;

    return (
        <>
            <Container className="simpleSliderComp" id="homeSection">
                <div className="sk-hero">
                    <div className="sk-copy">
                        <div className="sk-copy-text" key={`${lang}-${active}`}>
                            <div className="sk-eyebrow"><i></i>{slide.eyebrow}</div>
                            <h1>{slide.title}</h1>
                            <p>{slide.text}</p>
                        </div>
                        <div className="sk-tabs" role="tablist">
                            {slides.map((item, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    role="tab"
                                    aria-selected={index === active}
                                    className={index === active ? "sk-tab active" : "sk-tab"}
                                    onClick={() => setActive(index)}
                                >
                                    {copy[lang][index].label}
                                    {index === active && (
                                        <span className="sk-tab-progress" style={{ animationDuration: `${item.duration}ms` }} />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="sk-scene" ref={sceneRef} style={{ height: STAGE_HEIGHT * scale }} aria-hidden="true">
                        <div className="sk-stage" dir="ltr" style={{ transform: `scale(${scale})` }} key={`${lang}-${active}`}>
                            <Scene />
                        </div>
                    </div>
                </div>
            </Container>
        </>
    )
}
export default SmartKeyHero;
